import {formatNumber} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {
  AdminDashboardDrillColumn,
  AdminDashboardDrillData,
  AdminDashboardDrillResult,
  AdminDashboardDrillRowView,
  AdminDashboardDrillView
} from '@features/admin-panel/models/admin-dashboard-drill.models';

/**
 * The Admin dashboard's one drill dialog (ITP-E-159-03, Plan ITP-P-35 SPEC WEB-1; OCA-E-159 ui.tsx:136-212 DrillDialog):
 * a title bar with a close button, an optional subtitle and one table whose first column is the company. A company
 * name opens Company 360; "Evidence" closes the dialog with `{action: 'evidence', companyId}` so the card that opened
 * it can open the why panel. Opened only through AdminDashboardDrillService; the rows come from `data.load()`,
 * subscribed once when the dialog opens.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-drill-dialog',
  templateUrl: './admin-dashboard-drill-dialog.component.html',
  styleUrls: ['./admin-dashboard-drill-dialog.component.scss']
})
export class AdminDashboardDrillDialogComponent implements OnInit, OnDestroy {
  view: AdminDashboardDrillView | null = null;
  loading = true;
  failed = false;

  private loadSubscription: Subscription;

  constructor(@Inject(MAT_DIALOG_DATA) public data: AdminDashboardDrillData,
              private dialogRef: MatDialogRef<AdminDashboardDrillDialogComponent, AdminDashboardDrillResult>,
              private router: Router) {}

  ngOnInit(): void {
    this.loadSubscription = this.data.load().pipe(take(1)).subscribe({
      next: view => {
        const rows = view?.rows ?? [];
        this.view = {subtitle: view?.subtitle ?? '', rows, total: view?.total ?? rows.length};
        this.loading = false;
      },
      error: () => {
        this.failed = true;
        this.loading = false;
      },
      complete: () => {
        if (this.loading) {
          this.view = {subtitle: '', rows: [], total: 0};
          this.loading = false;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  /** The drill's own columns; the fixed first "Company" column is not among them. */
  get columns(): AdminDashboardDrillColumn[] {
    return this.data?.columns ?? [];
  }

  /** "Showing 25 of 1,234." when the server capped the list (Plan ITP-P-35 Q10), else ''. */
  get moreText(): string {
    const view = this.view;
    if (!view || view.total <= view.rows.length) {
      return '';
    }
    return `Showing ${formatNumber(view.rows.length, 'en-US', '1.0-0')} of ${formatNumber(view.total, 'en-US', '1.0-0')}.`;
  }

  /** A cell's alignment follows its column; a cell past the declared columns is right-aligned. */
  alignOf(index: number): 'start' | 'end' {
    return this.columns[index]?.align ?? 'end';
  }

  /** The company name: Company 360, then the dialog closes with no result. */
  openCompany(row: AdminDashboardDrillRowView): void {
    this.router.navigateByUrl('/admin-panel/company-360/' + row.companyId);
    this.dialogRef.close();
  }

  /** "Evidence": the dialog closes and the card opens the why panel for this company. */
  evidence(row: AdminDashboardDrillRowView): void {
    this.dialogRef.close({action: 'evidence', companyId: row.companyId});
  }

  close(): void {
    this.dialogRef.close();
  }
}
